import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import useUser from '../../hooks/useUser';

/**
 * A component that give access to children/component if criteria are met.
 */
export default function CheckPermissions({
  roles,
  permissions,
  access,
  enabled,
  auth,
  accessDeniedComponent: AccessDeniedComponent,
  allowedComponent: AllowedComponent,
  children,
  routes,
}) {
  const user = useUser();
  const { hasRole, hasPermission, isAdmin, isAuth } = user;

  const isRouteAllowed = useCallback(
    (route) =>
      route.enabled &&
      (isAdmin || // The user is an admin
        ((!route.auth || isAuth) && // The authentication is not needed or needed and user is auth
          (route.access == null ||
            (typeof route.access === 'function' && route.access(user)) ||
            (typeof route.access === 'boolean' && route.access)) && // an access function is supplied
          (!route.roles || hasRole(route.roles)) && // no roles or role met
          (!route.permissions || hasPermission(route.permissions)))), // no permission or permission met
    [hasPermission, hasRole, isAdmin, isAuth, user]
  );

  const isAllowed = useMemo(() => {
    if (routes) return routes.some((route) => isRouteAllowed(route));

    return isRouteAllowed({ auth, access, roles, permissions, enabled });
  }, [routes, isRouteAllowed, auth, access, roles, permissions, enabled]);

  if (isAllowed) {
    return AllowedComponent ?? children;
  }

  return AccessDeniedComponent ?? null;
}

CheckPermissions.propTypes = {
  roles: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  permissions: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  access: PropTypes.func,
  auth: PropTypes.bool,
  accessDeniedComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  allowedComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  children: PropTypes.node,
  // eslint-disable-next-line react/forbid-prop-types
  routes: PropTypes.arrayOf(PropTypes.object),
  enabled: PropTypes.bool,
};

CheckPermissions.defaultProps = {
  accessDeniedComponent: null,
  allowedComponent: null,
  auth: true,
  access: undefined,
  roles: undefined,
  permissions: undefined,
  routes: undefined,
  children: null,
  enabled: true,
};
